/**
 * CellPeekPopover — lightweight inspect surface anchored to a grid cell.
 * Click = inspect (this), not navigate. The full job page is one click away.
 *
 * Surfaces:
 *   • Customer · project · crew · current allocation (e.g. "Tue–Wed · 2 shifts")
 *   • 4 actions: Extend (modal), Reschedule, Open Job, Mark Complete
 *
 * Owns no state — purely presentational. Parent (CapacityGrid) controls the
 * action chain (which modal opens, navigation, completion).
 */
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import type { CapacityCell } from "@/data/scheduling/capacityEngine";
import { Expand, CalendarClock, ExternalLink, CheckCircle2, AlertTriangle, Undo2, ShieldAlert } from "lucide-react";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import { useAppData } from "@/data/AppDataContext";
import { useScheduling } from "@/data/scheduling/context";
import { getJobRiskSignals } from "@/data/scheduling/riskSignals";
import { Badge } from "@/components/ui/badge";
import type { SchedulingSuggestion } from "@/data/scheduling/suggestions";
import { Sparkles } from "lucide-react";

/** Translates a 0-100 crew fit score into a one-word interpretation. */
function fitLabel(score: number): string {
  if (score >= 85) return "strong fit";
  if (score >= 70) return "good fit";
  if (score >= 50) return "marginal";
  return "poor fit";
}

interface Props {
  cell: CapacityCell;
  children: React.ReactNode;
  onExtend: () => void;
  onReschedule: () => void;
  onConfirm?: () => void;
  onStartInstall?: () => void;
  onComplete: () => void;
  /** Provided when this cell has scheduling conflicts — surfaces "Resolve" as the primary action. */
  onResolveConflict?: () => void;
  /** Return this job to the Ready rail (clears scheduling fields). Hidden if not provided or if job is in-progress/completed. */
  onReturnToQueue?: () => void;
  /** Persisted reason text from a "booked with warning" placement. Surfaces as a visible amber strip. */
  warningNote?: string | null;
  /**
   * Optional single suggestion shown under the fitScore line. Provided by
   * the parent (PlanCockpit) which has the grid + job list needed to derive it.
   * Click on a "better_crew" suggestion opens the Inspector pre-filled with
   * the proposed crew — see onAcceptSuggestion.
   */
  suggestion?: SchedulingSuggestion | null;
  onAcceptSuggestion?: (s: SchedulingSuggestion) => void;
}

export function CellPeekPopover({ cell, children, onExtend, onReschedule, onConfirm, onStartInstall, onComplete, onResolveConflict, onReturnToQueue, warningNote, suggestion, onAcceptSuggestion }: Props) {
  const { salesOpportunities } = useAppData();
  const { eventLog, auditTrail } = useScheduling();
  const job = cell.job;
  if (!job) return <>{children}</>;

  // Lightweight risk signals — read-only summary over existing scheduling
  // attribution. Subtle outline chips; only render when thresholds are met.
  const risk = getJobRiskSignals(job.id, eventLog, auditTrail);

  const hasConflict = cell.state === "conflict" && cell.conflictCount > 1;

  const total = cell.totalShifts ?? 1;
  const start = parseISO(job.scheduledInstallDate);
  // End-day label assumes contiguous weekdays — engine span uses same rule.
  let end = start;
  for (let i = 1; i < total; i++) {
    end = addDays(end, 1);
    while (end.getDay() === 0 || end.getDay() === 6) end = addDays(end, 1);
  }
  const spanLabel = total === 1
    ? format(start, "EEE MMM d")
    : `${format(start, "EEE MMM d")}–${format(end, "EEE MMM d")} · ${total} shifts`;

  const crewLabel = job.assignmentType === "Internal Crew"
    ? job.assignedCrew
    : job.assignedSubcontractor;

  const isCompleted = job.currentStage === "Completed";

  // ── Upstream lifecycle context (read-only) ──
  const opp = job.opportunityId
    ? salesOpportunities.find(o => o.id === job.opportunityId)
    : undefined;
  const leadSource = opp?.leadSource;
  const saleAgeDays = opp?.wonAt
    ? differenceInDays(new Date(), parseISO(opp.wonAt))
    : null;
  const inRescission = job.rescissionStatus === "In Rescission";
  const rescissionEnds = job.rescissionEndsAt ? parseISO(job.rescissionEndsAt) : null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="w-full">{children}</div>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-72 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2.5 border-b border-border">
          <p className="text-sm font-semibold text-foreground truncate">{job.customerName}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {job.projectType} · {job.address.city}
          </p>
          <div className="mt-1.5 space-y-0.5">
            <p className="text-[11px] text-muted-foreground">
              <span className="text-foreground/80">Crew:</span> {crewLabel || "Unassigned"}
            </p>
            <p className="text-[11px] text-muted-foreground">
              <span className="text-foreground/80">Allocated:</span> {spanLabel}
            </p>
            {cell.fitScore !== null && (
              <p className="text-[11px] text-muted-foreground">
                <span className="text-foreground/80">Crew fit:</span>{" "}
                <span className="tabular-nums">{cell.fitScore}/100</span>
                <span className="ml-1 text-foreground/70">· {fitLabel(cell.fitScore)}</span>
              </p>
            )}
            {/* Lightweight Recommendation Layer — at most one suggestion line.
                Click on a "better_crew" hint opens the Inspector pre-filled
                with the proposed crew (no inline swap). */}
            {suggestion && !hasConflict && (
              suggestion.kind === "better_crew" && onAcceptSuggestion ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onAcceptSuggestion(suggestion); }}
                  className="w-full text-left mt-1 flex items-start gap-1 text-[11px] text-primary hover:underline focus:outline-none focus:underline"
                >
                  <Sparkles className="h-3 w-3 mt-[2px] shrink-0" />
                  <span className="leading-snug">{suggestion.message}</span>
                </button>
              ) : (
                <p className={`text-[11px] mt-1 flex items-start gap-1 ${suggestion.tone === "warning" ? "text-warning" : "text-muted-foreground"}`}>
                  <Sparkles className="h-3 w-3 mt-[2px] shrink-0" />
                  <span className="leading-snug">{suggestion.message}</span>
                </p>
              )
            )}
            {hasConflict && (
              <p className="text-[11px] text-destructive flex items-center gap-1 mt-1 font-medium">
                <AlertTriangle className="h-3 w-3" />
                Hard conflict — {cell.conflictCount} jobs assigned this day
              </p>
            )}
          </div>
          {/* ── Sale context (read-only upstream lifecycle) ── */}
          {(job.salesRep || leadSource || saleAgeDays != null || job.scopeSummary || inRescission) && (
            <div className="mt-2 pt-2 border-t border-border/60 space-y-0.5">
              {job.salesRep && (
                <p className="text-[11px] text-muted-foreground">
                  <span className="text-foreground/80">Sold by:</span> {job.salesRep}
                  {saleAgeDays != null && (
                    <span className="text-muted-foreground/70"> · signed {saleAgeDays}d ago</span>
                  )}
                </p>
              )}
              {leadSource && (
                <p className="text-[11px] text-muted-foreground">
                  <span className="text-foreground/80">Source:</span> {leadSource}
                </p>
              )}
              {job.scopeSummary && (
                <p className="text-[11px] text-muted-foreground truncate" title={job.scopeSummary}>
                  <span className="text-foreground/80">Scope:</span> {job.scopeSummary}
                </p>
              )}
              {inRescission && (
                <p className="text-[11px] text-warning flex items-center gap-1 mt-1 font-medium">
                  <ShieldAlert className="h-3 w-3" />
                  In rescission{rescissionEnds ? ` · clears ${format(rescissionEnds, "MMM d")}` : ""}
                </p>
              )}
            </div>
          )}
          {warningNote && !hasConflict && (
            <div className="mt-2 px-2 py-1.5 rounded border border-warning/40 bg-warning/[0.08] text-[11px] text-foreground flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 text-warning mt-[1px] shrink-0" />
              <span className="leading-snug">
                <span className="text-warning font-medium">Booked with warning:</span>{" "}
                <span className="text-muted-foreground">{warningNote}</span>
              </span>
            </div>
          )}
          {risk.hasAny && (
            <div className="mt-2 pt-2 border-t border-border/60 flex flex-wrap gap-1">
              {risk.reschedules >= 2 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-warning/40 text-warning">
                  Rescheduled {risk.reschedules}×
                </Badge>
              )}
              {risk.extensions >= 1 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-warning/40 text-warning">
                  Previously extended
                </Badge>
              )}
              {risk.conflicts >= 1 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-destructive/40 text-destructive">
                  Conflict-prone
                </Badge>
              )}
            </div>
          )}
        </div>
        {/* Primary action becomes "Resolve conflict" when this cell is in conflict. */}
        {hasConflict && onResolveConflict && (
          <div className="px-2 pt-2">
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs w-full"
              onClick={onResolveConflict}
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Resolve conflict
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-1 p-2">
          <Button
            size="sm"
            variant={hasConflict ? "outline" : "default"}
            className="h-8 text-xs"
            onClick={onExtend}
            disabled={isCompleted}
          >
            <Expand className="h-3 w-3 mr-1" />
            Extend
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onReschedule}
            disabled={isCompleted}
          >
            <CalendarClock className="h-3 w-3 mr-1" />
            Reschedule
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onConfirm}
            disabled={isCompleted || job.currentStage === "Scheduled Confirmed" || job.currentStage === "In Progress"}
          >
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onStartInstall}
            disabled={isCompleted || job.currentStage === "In Progress"}
          >
            Start Install
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="h-8 text-xs"
          >
            <Link to={`/jobs/${job.id}`}>
              <ExternalLink className="h-3 w-3 mr-1" />
              Open job
            </Link>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={onComplete}
            disabled={isCompleted}
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {isCompleted ? "Completed" : "Mark complete"}
          </Button>
        </div>
        {/* Return to queue — promoted to a full-width outline button so it
            reads as a real exit ramp, not a buried tertiary action. */}
        {onReturnToQueue && !isCompleted && cell.state !== "in_progress" && (
          <div className="px-2 pb-2 -mt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs w-full border-dashed text-foreground/80 hover:text-foreground hover:border-warning/50 hover:bg-warning/[0.06]"
              onClick={onReturnToQueue}
            >
              <Undo2 className="h-3 w-3 mr-1" />
              Return to ready queue
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
